import { Injectable, OnDestroy } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { FormsBase } from 'src/app/common-components/forms-base/forms-base';
import { containsCharactersValidator } from 'src/app/common-components/validators/contain-characters.validator';
import { ExtensionGroupDetailForm, ExtensionGroupDetailFormValue } from '../models/extension-group-detail-form';
import { ExtensionGroupDetailSaveModel } from '../models/extension-group-detail-save.model';
import { ExtensionGroupDetailView } from '../models/extension-group-detail-view.model';

@Injectable({
   providedIn: 'root',
})
export class ExtensionGroupDetailFormService extends FormsBase implements OnDestroy {
   private destroyed$ = new Subject<void>();

   constructor(fb: UntypedFormBuilder, private store: Store, private action: Actions) {
      super();
      const form: ExtensionGroupDetailForm = {
         extensionGroupId: fb.control(null),
         description: fb.control(null),
         account: fb.control(null),
         name: fb.control(null, [Validators.required, containsCharactersValidator()]),
         extensionGroupExtensions: fb.control(null, [this.noExtensionsValidator]),
         createUser: fb.control(null),
         createDate: fb.control(null),
         updateUser: fb.control(null),
         updateDate: fb.control(null),
      };
      this.form = fb.group(form);
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   updateExtensionGroup(extensionGroup: ExtensionGroupDetailView): void {
      if (!extensionGroup) {
         return;
      }

      const formValue: Partial<ExtensionGroupDetailFormValue> = {
         account: extensionGroup.account,
         description: extensionGroup.description,
         name: extensionGroup.name,
         extensionGroupExtensions: extensionGroup.extensions?.items.map((givenExtension) => {
            return {
               extensionGroupExtensionId: givenExtension.extensionGroupExtensionId,
               extensionId: givenExtension.extension.extensionId,
               name: givenExtension.extension.name,
               number: givenExtension.extension.number,
               type: givenExtension.extension.type,
            };
         }),
      };
      this.form.patchValue(formValue);

      this.markAsUntouchedAndPristine();
   }

   getValue(extensionGroupId: number | null): ExtensionGroupDetailSaveModel {
      const value = this.form.value as ExtensionGroupDetailFormValue;
      return {
         accountId: value.account?.accountId ?? null,
         extensionGroupId: extensionGroupId,
         extensionGroupExtensions:
            value.extensionGroupExtensions?.map((extension) => {
               return {
                  extensionGroupExtensionId: extension.extensionGroupExtensionId || null,
                  extensionGroupId: extensionGroupId,
                  extensionId: extension.extensionId,
                  fields: [],
               };
            }) || null,
         name: value.name,
         description: value.description ?? null,
      };
   }

   noExtensionsValidator(control: AbstractControl): ValidationErrors | null {
      const isEmpty = !control.value || (Array.isArray(control.value) && control.value.length === 0);
      return isEmpty ? { empty: true } : null;
   }
}
